<template>
  <div class="plan-service-stage">
    <div class="plan-service-stage__unit">
      <div class="plan-service-stage__row">
        <input-text
          v-if="isEditName"
          v-bind:value="stageName"
          v-on:input="changeName"
          ref="inputName"
          v-on:blur="editName(false)"
          maxlength="100"
        />
        <span v-else>{{ stageName }}</span>
        <button
          class="plan-service-stage__icon-edit"
          type="button"
          v-on:click="editName(true)"
        />
      </div>
      <div
        class="plan-service-stage__cell"
        data-label="Удалить">
        <button
          type="button"
          class="plan-service-stage__icon-delete"
          v-on:click="emitDelete"
        />
      </div>
    </div>
    <plan-service-table-row
      v-for="({ item, isDrag }, index) in services"
      v-bind:key="item.id || item.key"
      v-bind:item="item"
      v-bind:discount-type="discountType"
      v-bind:is-show-progress="isShowPlanProgress"
      v-bind:is-drag="isDrag"
      v-on:change="changeRow(index, $event)"
      v-on:delete="deleteRow(index)"
      v-on:drag-enter="onDragEnter"
      v-on:drag-start="onDragStart"
      v-on:drag-end="onDragEnd"
      v-on:drop="onDrop"
    />
    <div
      class="plan-service-table__last"
      :class="{ 'plan-service-table__drag-selected-element': isDropLast }"
      v-on:drop="onMoveToEnd"
      v-on:dragenter="onDragEnterLast"
      v-on:dragover="onDragOverLast"
      v-on:dragleave="onDragLeaveLast"
    />
    <div class="plan-service-stage__bottom">
      <button
        class="plan-service-stage__button-add"
        type="button"
        v-on:click="emitAddService">
        добавить услуги
      </button>
      <div v-if="isShowResult" class="plan-service-table__results">
        <p>
          Итого: <b>
            <the-rub
              v-bind:show-kop="$options.SHOW_KOP_AUTO"
              v-bind:kop="aggregateTotal.amount"
              currency="тнг" />
          </b>
        </p>
        <p>
          Скидка:
          <input-total-discount
            v-bind:disabled="false"
            v-bind:initial-discount-type="discountType"
            v-bind:initial-discount="aggregateTotal.discountWithoutFreeServices"
            v-bind:max-discount="aggregateTotal.amountWithoutFreeServices"
            v-bind:value="aggregateTotal.discount"
            v-on:change="onTotalDiscountChanged"
          />
        </p>
        <p>
          С учетом скидки: <b>
            <the-rub
              v-bind:show-kop="$options.SHOW_KOP_AUTO"
              v-bind:kop="aggregateTotal.final"
              currency="тнг"
            />
          </b>
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import InputText from '@/components/InputText.vue';
import InputTotalDiscount from '@/components/InputTotalDiscount.vue';
import PlanServiceTableRow from '@/components/PlanServiceTableRow.vue';
import TheRub, { SHOW_KOP_AUTO } from '@/components/TheRub.vue';

/**
 * @description Название события добавления услуги к этапу плана лечения.
 * @type {string}
 */
const NAME_EVENT_ADD_SERVICE = 'add-service';

/**
 * @description Название события изменения суммы общей скидки.
 * @type {string}
 */
const NAME_EVENT_CHANGE_TOTAL_DISCOUNT = 'change-total-discount';

/**
 * @description Название события удаления этапа лечения.
 * @type {string}
 */
const NAME_EVENT_DELETE_STAGE = 'delete';

/**
 * @description Название события завершения перетаскивания.
 * @type {string}
 */
const NAME_EVENT_DRAG_END = 'drag-end';

/**
 * @description Название события попадания перетаскиваемого элемента в допустимую цель.
 * @type {string}
 */
const NAME_EVENT_DRAG_ENTER = 'drag-enter';

/**
 * @description Название события начала перетаскивания.
 * @type {string}
 */
const NAME_EVENT_DRAG_START = 'drag-start';

/**
 * @description Название события сброса элемента в допустимую зону.
 * @type {string}
 */
const NAME_EVENT_DROP = 'drop';

/**
 * @description Примесь перетаскивания.
 */
const dragAndDropMixin = {
  computed: {
    services() {
      return this.stageServices.map((item) => ({
        item,
        isDrag: item === this.dragCandidate && item !== this.dragTarget,
      }));
    },
  },
  data() {
    return {
      isDropLast: false,
      dragTarget: null,
    };
  },
  methods: {
    /**
     * @description Обработать попадание перетаскиваемого элемента в допустимую цель.
     * @param {any} item
     * @returns {void}
     */
    onDragEnter(item) {
      this.$emit(NAME_EVENT_DRAG_ENTER, item);
    },
    /**
     * @description Обработать начало перетаскивания.
     * @param {any} item
     * @returns {void}
     */
    onDragStart(item) {
      this.dragTarget = item;
      this.$emit(NAME_EVENT_DRAG_START, item);
    },
    /**
     * @description Обработать завершение перетаскивания.
     * @returns {void}
     */
    onDragEnd() {
      this.dragTarget = null;
      this.$emit(NAME_EVENT_DRAG_END);
    },
    /**
     * @description Обработать сброс элемента в допустимую зону.
     * @param {any} item
     * @returns {void}
     */
    onDrop(item) {
      this.$emit(NAME_EVENT_DROP, item);
    },
    /**
     * @description Обработать перемещение элемента в конец списка.
     * @returns {void}
     */
    onMoveToEnd() {
      this.$emit('move-end');
      this.isDropLast = false;
      this.dragTarget = null;
    },
    onDragEnterLast() {
      this.isDropLast = true;
      this.$emit('input-drag-last', this.isDropLast);
    },
    onDragOverLast(e) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    onDragLeaveLast() {
      this.isDropLast = false;
      this.$emit('input-drag-last', this.isDropLast);
    },
  },
  props: {
    dragCandidate: { default: null, type: Object },
  },
};

/**
 * @description Этап плана лечения
 */
export default {
  SHOW_KOP_AUTO,
  components: {
    InputText,
    PlanServiceTableRow,
    TheRub,
    InputTotalDiscount,
  },
  computed: {
    /**
     * @description Показывать итоги.
     * @returns {boolean}
     */
    isShowResult() {
      return this.isShowTotal && this.stageServices.length > 0;
    },
  },
  data() {
    return {
      /**
       * @description Изменение имени этапа.
       * @type {boolean}
       */
      isEditName: false,
    };
  },
  name: 'PatientPlanStage',
  methods: {
    /**
     * @description Изменение имени этапа.
     * @param {string} value
     * @returns {void}
     */
    changeName(value) {
      this.$emit('input-name', value);
    },
    /**
     * @description Изменение строки списка услуг.
     * @param {number} index Номер строки.
     * @param {any}    value Строка.
     */
    changeRow(index, value) {
      this.$emit(
        'input-rows',
        this.stageServices.map(
          (candidate, candidateIndex) => (index === candidateIndex
            ? value
            : candidate),
        ),
      );
    },
    /**
     * @description Удаление строки списка услуг.
     * @param {number} index Номер строки.
     * @returns {void}
     */
    deleteRow(index) {
      this.$emit(
        'input-rows',
        this.stageServices.filter(
          (_, candidateIndex) => (index !== candidateIndex),
        ),
      );
    },
    /**
     * @description Редактировать название этапа.
     * @param {boolean} isEdit
     */
    editName(isEdit) {
      this.isEditName = isEdit;
      if (isEdit) {
        this.$nextTick(() => {
          this.$refs.inputName.$el.focus();
        });
      }
    },
    /**
     * @description Отправить событие удаления этапа.
     */
    emitDelete() {
      this.$emit(NAME_EVENT_DELETE_STAGE);
    },
    /**
     * @description Отправить событие добавления новой услуги этапу.
     */
    emitAddService() {
      this.$emit(NAME_EVENT_ADD_SERVICE);
    },
    /**
     * @description Обработать изменение общей скидки.
     * @param {number} value
     * @returns {void}
     */
    onTotalDiscountChanged(value) {
      this.$emit(NAME_EVENT_CHANGE_TOTAL_DISCOUNT, value);
    },
  },
  mixins: [dragAndDropMixin],
  props: {
    /**
     * @description Суммарные итоги.
     * @type {Object}
     */
    aggregateTotal: {
      required: true,
      type: Object,
    },
    /**
     * @description Показывать прогресс.
     * @type {boolean}
     */
    isShowPlanProgress: {
      required: true,
      type: Boolean,
    },
    /**
     * @description Показывать итоги.
     * @type {boolean}
     */
    isShowTotal: {
      required: true,
      type: Boolean,
    },
    /**
     * @description Тип скидки.
     * @type {string}
     */
    discountType: {
      required: true,
      type: String,
    },
    /**
     * @description Название этапа
     * @type { string }
     */
    stageName: {
      required: true,
      type: String,
    },
    /**
     * @description Услуги этапа
     * @type { Array }
     */
    stageServices: {
      required: true,
      type: Array,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";
.plan-service-stage {
  margin: 0 0 20px 0;
  &__cell {
    padding: 12px 8px;
  }
  &__unit {
    display: grid;
    grid-template-columns: 1fr 43px;
    align-items: center;
    padding: 12px 0;
  }
  &__row {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 0 8px;
    label {
      width: 90%;
    }
    span {
      font-weight: 700;
    }
  }
  &__icon-edit {
    background-image: url("@/assets/img/pen-grey.svg");
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    width: 15px;
    min-width: 15px;
    height: 15px;
    min-height: 15px;
    padding: 0;
    cursor: pointer;
    &:hover {
      background-image: url("@/assets/img/pen-blue.svg");
    }
  }
  &__icon-delete {
    display: block;
    background-size: contain;
    background-repeat: no-repeat;
    width: 12px;
    height: 12px;
    cursor: pointer;
    background-image: url("@/assets/img/exit-grey.svg");
    &:hover {
      background-image: url("@/assets/img/exit-blue.svg");
    }
  }
  &__button-add {
    margin-top: 20px;
    @include buttons.btn-transparent(100%, 40px, 16px);
    max-width: 160px;
    &::before {
      content: "+";
      margin-right: 4px;
    }
  }
  &__bottom {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 5px 10px;
  }
}

@media screen and (max-width: 520px) {
  .plan-service-table {
    &__results {
      margin-left: 0;
    }
  }
}
</style>
